import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Bell, User, LogOut, Settings, Moon, Sun, Menu,
    ChevronDown, Search, MapPin, ShoppingCart, X, CheckCheck, Package
} from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { useTheme } from '@/app/providers';
import { UserRole } from '@/types/user.types';
import { Button } from '@/components/ui/Button';
import { cn } from '../../lib/utils';
import { useState, useRef, useEffect } from 'react';
import { useCart } from '@/context/CartContext';
import { useQuery } from '@tanstack/react-query';
import { notificationService } from '@/services/notificationService';
import { notificationKeys, useUnreadCount, useMarkAllAsRead } from '@/hooks/useNotifications';
import { trackSearchPerformed, trackUserLoggedOut } from '@/lib/analytics/events';

const pageTitles: Record<string, string> = {
    '/home': 'Home',
    '/cart': 'Shopping Cart',
    '/orders': 'My Orders',
    '/profile': 'My Profile',
    '/checkout': 'Checkout',
    '/farmer': 'Dashboard',
    '/farmer/products': 'My Products',
    '/farmer/orders': 'Incoming Orders',
    '/farmer/dispatch': 'Dispatch',
    '/farmer/analytics': 'Analytics',
    '/farmer/messages': 'Messages',
    '/farmer/profile': 'My Profile',
    '/delivery': 'Dashboard',
    '/delivery/requests': 'Delivery Requests',
    '/delivery/active': 'Active Deliveries',
    '/delivery/history': 'Delivery History',
    '/delivery/earnings': 'Earnings',
    '/delivery/profile': 'My Profile',
    '/supplier': 'Dashboard',
    '/supplier/catalog': 'Catalog',
    '/supplier/orders': 'Incoming Orders',
    '/supplier/dispatch': 'Dispatch',
    '/supplier/profile': 'My Profile',
    '/admin': 'Dashboard',
    '/settings': 'Settings',
};

interface UnifiedHeaderProps {
    onMenuClick?: () => void;
    isMobile?: boolean;
}

export const UnifiedHeader = ({ onMenuClick, isMobile }: UnifiedHeaderProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, clearAuth } = useAuthStore();
    const { theme, toggleTheme } = useTheme();
    const { toggleCart, itemCount } = useCart();

    const [showProfileMenu, setShowProfileMenu] = useState(false);
    const [showNotifications, setShowNotifications] = useState(false);
    const [showLocationModal, setShowLocationModal] = useState(false);
    const [locationInput, setLocationInput] = useState('');
    const [currentLocation, setCurrentLocation] = useState(
        () => localStorage.getItem('user_location') || '123 Main St, New York'
    );
    const [searchQuery, setSearchQuery] = useState('');

    const profileMenuRef = useRef<HTMLDivElement>(null);
    const notificationsRef = useRef<HTMLDivElement>(null);

    const currentPageTitle = pageTitles[location.pathname] || 'Dashboard';

    // Shared cache keys with useNotifications so invalidations propagate everywhere.
    const { data: notifData } = useQuery({
        queryKey: notificationKeys.list({ limit: 10 }),
        queryFn: () => notificationService.getNotifications({ limit: 10 }),
        enabled: showNotifications,
        refetchInterval: showNotifications ? 30000 : false,
    });

    const { data: unreadData } = useUnreadCount();

    const markAllReadMutation = useMarkAllAsRead();

    const handleSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && searchQuery.trim()) {
            trackSearchPerformed(searchQuery, 'unified_header');
            navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
            setSearchQuery('');
        }
    };

    const handleSaveLocation = () => {
        if (locationInput.trim()) {
            localStorage.setItem('user_location', locationInput.trim());
            setCurrentLocation(locationInput.trim());
        }
        setShowLocationModal(false);
        setLocationInput('');
    };

    const isDark = theme === 'dark';

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
                setShowProfileMenu(false);
            }
            if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
                setShowNotifications(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLogout = () => {
        trackUserLoggedOut('manual');
        clearAuth();
        navigate('/login');
    };

    const getProfilePath = () => {
        switch (user?.role) {
            case UserRole.FARMER: return '/farmer/profile';
            case UserRole.DELIVERY_AGENT: return '/delivery/profile';
            case UserRole.SUPPLIER: return '/supplier/profile';
            case UserRole.ADMIN: return '/admin/profile';
            default: return '/profile';
        }
    };

    const unreadCount = unreadData?.count ?? 0;
    const notifications = notifData?.notifications || [];

    return (
        <>
        <header className={cn(
            "sticky top-0 z-30 transition-all duration-300 px-4 py-4 lg:px-8 border-b",
            isDark ? "bg-secondary-900/80 border-secondary-800" : "bg-white/80 border-secondary-100",
            "backdrop-blur-xl"
        )}>
            <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">

                {/* Left Side */}
                <div className="flex items-center gap-4 flex-1">
                    {isMobile && (
                        <Button variant="ghost" size="sm" onClick={onMenuClick} className="lg:hidden p-2">
                            <Menu size={24} />
                        </Button>
                    )}
                    <div className="flex-1">
                        <h1 className="text-xl font-bold text-secondary-900 dark:text-white">
                            {user?.role === UserRole.CUSTOMER && location.pathname === '/home' ? 'Welcome Back!' : currentPageTitle}
                        </h1>
                        {user?.role === UserRole.CUSTOMER && location.pathname === '/home' && (
                            <div className="flex items-center gap-1 text-sm text-secondary-500 dark:text-secondary-400">
                                <MapPin size={14} />
                                <span>{currentLocation}</span>
                                <button
                                    className="text-primary-500 font-medium cursor-pointer hover:text-primary-600 ml-1 bg-transparent border-none p-0"
                                    onClick={() => { setLocationInput(currentLocation); setShowLocationModal(true); }}
                                >
                                    Change
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Side */}
                <div className="flex items-center gap-3">

                    {/* Search Bar */}
                    <div className="relative hidden md:block w-64 group">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary-400 group-focus-within:text-primary-500 transition-colors" size={18} />
                        <input
                            type="text"
                            placeholder="Search products... (Enter)"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyDown={handleSearch}
                            className={cn(
                                "w-full pl-10 pr-4 py-2.5 rounded-xl border-none outline-none transition-all",
                                isDark
                                    ? "bg-secondary-800 text-white focus:ring-4 focus:ring-primary-500/20"
                                    : "bg-secondary-100/80 text-secondary-900 focus:ring-4 focus:ring-primary-500/15"
                            )}
                        />
                    </div>

                    {/* Theme Toggle */}
                    <button
                        onClick={toggleTheme}
                        className={cn(
                            "w-10 h-10 rounded-xl flex items-center justify-center transition-colors",
                            isDark ? "text-yellow-400 hover:bg-secondary-800" : "text-secondary-500 hover:bg-secondary-100 hover:text-primary-500"
                        )}
                    >
                        {isDark ? <Sun size={20} /> : <Moon size={20} />}
                    </button>

                    {/* Cart */}
                    <button
                        onClick={toggleCart}
                        className={cn(
                            "w-10 h-10 rounded-xl flex items-center justify-center transition-colors relative",
                            isDark ? "text-secondary-400 hover:bg-secondary-800 hover:text-white" : "text-secondary-500 hover:bg-secondary-100 hover:text-primary-500"
                        )}
                    >
                        <ShoppingCart size={20} />
                        {itemCount > 0 && (
                            <span className="absolute -top-1 -right-1 bg-primary-500 text-white text-[10px] font-bold h-5 min-w-[20px] px-1 flex items-center justify-center rounded-full border-2 border-white dark:border-secondary-900">
                                {itemCount}
                            </span>
                        )}
                    </button>

                    {/* Notifications */}
                    <div className="relative" ref={notificationsRef}>
                        <button
                            onClick={() => setShowNotifications(!showNotifications)}
                            className={cn(
                                "w-10 h-10 rounded-xl flex items-center justify-center transition-colors relative",
                                isDark ? "text-secondary-400 hover:bg-secondary-800 hover:text-white" : "text-secondary-500 hover:bg-secondary-100 hover:text-primary-500"
                            )}
                        >
                            <Bell size={20} />
                            {unreadCount > 0 ? (
                                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold h-5 min-w-[20px] px-1 flex items-center justify-center rounded-full border-2 border-white dark:border-secondary-900">
                                    {unreadCount > 99 ? '99+' : unreadCount}
                                </span>
                            ) : (
                                <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full border-2 border-white dark:border-secondary-900" />
                            )}
                        </button>

                        <AnimatePresence>
                            {showNotifications && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                    transition={{ duration: 0.2 }}
                                    className={cn(
                                        "absolute right-0 top-full mt-2 w-80 rounded-2xl shadow-xl border overflow-hidden",
                                        isDark ? "bg-secondary-900 border-secondary-800" : "bg-white border-secondary-100"
                                    )}
                                >
                                    <div className="px-4 py-3 border-b border-secondary-100 dark:border-secondary-800 flex items-center justify-between">
                                        <h3 className="font-bold text-secondary-900 dark:text-white">Notifications</h3>
                                        {unreadCount > 0 && (
                                            <button
                                                onClick={() => markAllReadMutation.mutate()}
                                                className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium"
                                            >
                                                <CheckCheck size={14} />
                                                Mark all read
                                            </button>
                                        )}
                                    </div>

                                    <div className="max-h-80 overflow-y-auto divide-y divide-secondary-100 dark:divide-secondary-800">
                                        {notifications.length === 0 ? (
                                            <div className="p-6 text-center text-secondary-500 dark:text-secondary-400">
                                                <Package size={32} className="mx-auto mb-2 opacity-40" />
                                                <p className="text-sm">No notifications yet</p>
                                            </div>
                                        ) : (
                                            notifications.map((n) => (
                                                <div
                                                    key={n.id}
                                                    className={cn(
                                                        "px-4 py-3 hover:bg-secondary-50 dark:hover:bg-secondary-800/50 transition-colors",
                                                        !n.is_read && "bg-primary-50/50 dark:bg-primary-900/10"
                                                    )}
                                                >
                                                    <div className="flex items-start gap-3">
                                                        <div className={cn(
                                                            "w-2 h-2 rounded-full mt-2 shrink-0",
                                                            !n.is_read ? "bg-primary-500" : "bg-secondary-300 dark:bg-secondary-600"
                                                        )} />
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm text-secondary-900 dark:text-white line-clamp-2">
                                                                {n.message}
                                                            </p>
                                                            <p className="text-xs text-secondary-400 mt-1">
                                                                {new Date(n.created_at).toLocaleString()}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>

                                    <div className="px-4 py-3 border-t border-secondary-100 dark:border-secondary-800">
                                        <button
                                            onClick={() => { navigate('/settings?tab=notifications'); setShowNotifications(false); }}
                                            className="w-full text-center text-sm text-primary-600 hover:text-primary-700 font-medium"
                                        >
                                            View all notifications
                                        </button>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* Profile Dropdown */}
                    <div className="relative" ref={profileMenuRef}>
                        <button
                            onClick={() => setShowProfileMenu(!showProfileMenu)}
                            className="flex items-center gap-2 md:gap-3 p-1 pr-2 md:pr-3 rounded-xl hover:bg-secondary-50 dark:hover:bg-secondary-800 transition-colors"
                        >
                            <div className="w-10 h-10 rounded-xl bg-primary-500 flex items-center justify-center text-white font-bold shadow-md overflow-hidden">
                                {user?.profileImage
                                    ? <img src={user.profileImage} alt="Profile" className="w-full h-full object-cover" />
                                    : (user?.firstName?.[0] || 'U')}
                            </div>
                            {!isMobile && (
                                <div className="text-left hidden md:block">
                                    <p className="text-sm font-bold text-secondary-900 dark:text-white leading-none">
                                        {user?.firstName || 'User'}
                                    </p>
                                    <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1 capitalize">
                                        {user?.role?.replace('_', ' ') || 'Guest'}
                                    </p>
                                </div>
                            )}
                            <ChevronDown size={16} className="text-secondary-400" />
                        </button>

                        <AnimatePresence>
                            {showProfileMenu && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                    transition={{ duration: 0.2 }}
                                    className={cn(
                                        "absolute right-0 top-full mt-2 w-56 rounded-2xl shadow-xl border overflow-hidden p-2",
                                        isDark ? "bg-secondary-900 border-secondary-800" : "bg-white border-secondary-100"
                                    )}
                                >
                                    <div className="px-3 py-2 border-b border-secondary-100 dark:border-secondary-800 mb-2 md:hidden">
                                        <p className="font-bold text-secondary-900 dark:text-white">{user?.firstName || 'User'}</p>
                                        <p className="text-xs text-secondary-500 dark:text-secondary-400 capitalize">
                                            {user?.role?.replace('_', ' ') || 'Guest'}
                                        </p>
                                    </div>

                                    <button
                                        onClick={() => { navigate(getProfilePath()); setShowProfileMenu(false); }}
                                        className={cn(
                                            "w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-colors",
                                            isDark ? "text-secondary-300 hover:bg-secondary-800 hover:text-white" : "text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900"
                                        )}
                                    >
                                        <User size={18} /> Profile
                                    </button>
                                    <button
                                        onClick={() => { navigate('/settings'); setShowProfileMenu(false); }}
                                        className={cn(
                                            "w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-colors",
                                            isDark ? "text-secondary-300 hover:bg-secondary-800 hover:text-white" : "text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900"
                                        )}
                                    >
                                        <Settings size={18} /> Settings
                                    </button>

                                    <div className="h-px bg-secondary-100 dark:bg-secondary-800 my-2" />

                                    <button
                                        onClick={handleLogout}
                                        className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                    >
                                        <LogOut size={18} /> Logout
                                    </button>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        </header>

        {/* Location Modal */}
        <AnimatePresence>
            {showLocationModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                        onClick={() => setShowLocationModal(false)}
                    />
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        transition={{ duration: 0.2 }}
                        className={cn(
                            "relative w-full max-w-sm rounded-2xl shadow-xl p-6",
                            isDark ? "bg-secondary-900 border border-secondary-800" : "bg-white"
                        )}
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-bold text-lg text-secondary-900 dark:text-white">Change Location</h3>
                            <button
                                onClick={() => setShowLocationModal(false)}
                                className="w-8 h-8 rounded-lg flex items-center justify-center text-secondary-400 hover:bg-secondary-100 dark:hover:bg-secondary-800"
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <p className="text-sm text-secondary-500 dark:text-secondary-400 mb-4">
                            Enter your delivery address or area to get products near you.
                        </p>
                        <div className="relative mb-4">
                            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary-400" size={18} />
                            <input
                                type="text"
                                value={locationInput}
                                onChange={(e) => setLocationInput(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSaveLocation()}
                                placeholder="e.g. 15 Oxford St, Accra"
                                autoFocus
                                className={cn(
                                    "w-full pl-10 pr-4 py-3 rounded-xl border outline-none focus:ring-2 focus:ring-primary-500 transition-all",
                                    isDark
                                        ? "bg-secondary-800 border-secondary-700 text-white"
                                        : "bg-secondary-50 border-secondary-200 text-secondary-900"
                                )}
                            />
                        </div>
                        <div className="flex gap-3">
                            <Button variant="outline" className="flex-1" onClick={() => setShowLocationModal(false)}>
                                Cancel
                            </Button>
                            <Button className="flex-1" onClick={handleSaveLocation}>
                                Save Location
                            </Button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
        </>
    );
};
